import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
  ActivityIndicator,
  Dimensions,
  Image,
} from 'react-native';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import Icon from 'react-native-vector-icons/MaterialIcons';
import DateTimePicker from '../components/DateTimePicker';
import { listEvents, createEvent as createEventSupabase, updateEvent as updateEventSupabase } from '../services/database';
import { approveDraftAsEvent } from '../services/socialSyncService';
import { getColors, getShadows, typography, spacing, borderRadius } from '../styles/theme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const QUICK_PAIR_GREEN = '#84D288';
const QUICK_PAIR_SHADOW = {
  shadowColor: '#76AF87',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 1,
  shadowRadius: 0,
  elevation: 8,
};

const CheckerBackground = React.memo(({ isDarkMode }) => {
  const squareSize = 40;
  const cols = Math.ceil(SCREEN_WIDTH / squareSize) + 1;
  const rows = 6;
  const baseOpacity = isDarkMode ? 0.04 : 0.03;
  const squares = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if ((r + c) % 2 === 0) {
        squares.push(
          <View
            key={`${r}-${c}`}
            style={{
              position: 'absolute',
              top: r * squareSize,
              left: c * squareSize,
              width: squareSize,
              height: squareSize,
              backgroundColor: isDarkMode ? '#FFFFFF' : '#000000',
              opacity: baseOpacity,
              borderRadius: 2,
            }}
          />
        );
      }
    }
  }
  return <View style={{ position: 'absolute', left: 0, right: 0, top: 0, height: rows * squareSize }} pointerEvents="none">{squares}</View>;
});

const CreateEventScreen = ({ navigation, route }) => {
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const themeShadows = getShadows(isDarkMode);
  const [loading, setLoading] = useState(false);
  const [clubData, setClubData] = useState(null);
  const [eventCount, setEventCount] = useState(0);
  const [isFreeTier, setIsFreeTier] = useState(true);
  const FREE_TIER_EVENT_LIMIT = 3;

  const isEditMode = route.params?.mode === 'edit';
  const existingEvent = route.params?.eventData;
  const socialDraftId = route.params?.socialDraftId;
  const socialDraft = route.params?.socialDraft;
  const socialClubId = route.params?.clubId;
  const prefillEvent = route.params?.prefillEvent;
  const socialPost = route.params?.socialPost;

  const getInitialDate = () => {
    if (existingEvent?.eventDate) return new Date(existingEvent.eventDate);
    if (socialDraft?.parsedDate) {
      try {
        const d = new Date(socialDraft.parsedDate);
        return isNaN(d.getTime()) ? new Date() : d;
      } catch {
        return new Date();
      }
    }
    return new Date();
  };

  const getInitialTime = () => {
    if (existingEvent?.eventTime) {
      const [hours, minutes] = existingEvent.eventTime.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
      return date;
    }
    if (socialDraft?.parsedDate) {
      try {
        const d = new Date(socialDraft.parsedDate);
        if (!isNaN(d.getTime())) return d;
      } catch {}
    }
    return new Date();
  };

  const getInitialEndTime = () => {
    if (existingEvent?.endTime) return new Date(existingEvent.endTime);
    const defaultEnd = new Date(getInitialTime());
    defaultEnd.setHours(defaultEnd.getHours() + 2);
    return defaultEnd;
  };

  const hasPrefill = !!(prefillEvent || socialPost);

  const [title, setTitle] = useState(existingEvent?.title || prefillEvent?.title || socialDraft?.title || '');
  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const [selectedTime, setSelectedTime] = useState(getInitialTime());
  const [selectedEndTime, setSelectedEndTime] = useState(getInitialEndTime());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [location, setLocation] = useState(existingEvent?.location || prefillEvent?.location || socialDraft?.parsedLocation || '');
  const [locationAddress, setLocationAddress] = useState(existingEvent?.locationAddress || prefillEvent?.location || socialDraft?.parsedLocation || '');
  const [locationLatitude, setLocationLatitude] = useState(existingEvent?.locationLatitude || null);
  const [locationLongitude, setLocationLongitude] = useState(existingEvent?.locationLongitude || null);
  const [useClubLocation, setUseClubLocation] = useState(!existingEvent?.location && !prefillEvent?.location && !socialDraft?.parsedLocation && !socialPost);
  const [eventType, setEventType] = useState(existingEvent?.eventType || prefillEvent?.eventType || 'CASUAL');
  const [description, setDescription] = useState(existingEvent?.description || prefillEvent?.description || socialPost?.caption || socialDraft?.description || '');
  const [capacity, setCapacity] = useState(existingEvent?.capacity ? existingEvent.capacity.toString() : prefillEvent?.capacity ? String(prefillEvent.capacity) : '');
  const [socialSourceUrl, setSocialSourceUrl] = useState(socialPost?.permalink || null);
  const [socialSourceImageUrl, setSocialSourceImageUrl] = useState(socialPost?.media_url || socialPost?.mediaUrl || null);
  const [isPaid, setIsPaid] = useState(existingEvent?.isPaid || false);
  const [price, setPrice] = useState(existingEvent?.price ? existingEvent.price.toString() : '');
  const [saveAsLocation, setSaveAsLocation] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [checkInMode, setCheckInMode] = useState(
    existingEvent?.check_in_mode ?? existingEvent?.checkInMode ?? 'OFF'
  );
  const [checkInRadius, setCheckInRadius] = useState(
    existingEvent?.check_in_radius ?? existingEvent?.checkInRadius ?? 200
  );

  useEffect(() => {
    fetchClubData();
  }, []);

  useEffect(() => {
    if (clubData) {
      setIsFreeTier(!clubData.tier || clubData.tier === 'FREE');
      fetchEventCount();
    }
  }, [clubData]);

  // Update useClubLocation when clubData loads in edit mode
  useEffect(() => {
    if (isEditMode && existingEvent && clubData) {
      // Check if event location matches club address
      const isUsingClubAddress = existingEvent.location === clubData.address;
      setUseClubLocation(isUsingClubAddress);
      if (!isUsingClubAddress) {
        setLocation(existingEvent.location);
      }
    }
  }, [clubData, isEditMode]);

  const fetchClubData = async () => {
    try {
      if (appUser) {
        const userData = {
          id: appUser.id,
          clubName: appUser.clubName,
          address: appUser.address,
          latitude: appUser.latitude,
          longitude: appUser.longitude,
          tier: appUser.tier,
          claimedBy: appUser.claimedBy,
        };
        setClubData(userData);
        if (userData?.address && !hasPrefill && !isEditMode && !socialDraft) {
          setLocation(userData.address);
        }
      }
    } catch (error) {
      console.error('Error fetching club data:', error);
    }
  };

  const fetchEventCount = async () => {
    if (!clubData?.id) return;
    try {
      const events = await listEvents({ clubId: clubData.id });
      setEventCount((events || []).length);
    } catch (error) {
      console.error('Error fetching event count:', error);
    }
  };

  const handleCreateEvent = async () => {
    // Validation
    if (!title.trim()) {
      showToast('Please enter an event title', 'error');
      return;
    }

    // Check if club is claimed
    if (!clubData?.claimedBy) {
      showToast('Please claim your club first before creating events', 'error');
      navigation.navigate('UserDetail', { userId: clubData.id });
      return;
    }

    // Check tier limits for free tier
    if (isFreeTier && !isPaid) {
      if (eventCount >= FREE_TIER_EVENT_LIMIT) {
        showToast(`Free tier is limited to ${FREE_TIER_EVENT_LIMIT} events. Upgrade to Pro for unlimited events.`, 'error');
        navigation.navigate('UpgradeClub', { clubId: clubData.id });
        return;
      }
    }

    // Check if trying to create paid event without Pro tier
    if (isPaid && isFreeTier) {
      showToast('Paid events require Pro tier. Please upgrade to Pro.', 'error');
      navigation.navigate('UpgradeClub', { clubId: clubData.id });
      return;
    }

    setLoading(true);

    try {
      // Combine date and time into ISO string
      const combinedDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        selectedTime.getHours(),
        selectedTime.getMinutes()
      );
      const eventDateTime = combinedDate.toISOString();
      
      // Format time as HH:MM
      const hours = selectedTime.getHours().toString().padStart(2, '0');
      const minutes = selectedTime.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes}`;
      
      // Calculate endTime (combine date with endTime)
      const combinedEndDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        selectedEndTime.getHours(),
        selectedEndTime.getMinutes()
      );
      const eventEndDateTime = combinedEndDate.toISOString();

      if (isEditMode) {
        // UPDATE existing event
        const updates = {
          title: title.trim(),
          event_date: eventDateTime,
          event_time: formattedTime,
          end_time: eventEndDateTime,
          location: useClubLocation ? clubData.address : location.trim(),
          location_address: useClubLocation ? clubData.address : locationAddress.trim() || location.trim(),
          location_latitude: useClubLocation ? clubData.latitude : locationLatitude,
          location_longitude: useClubLocation ? clubData.longitude : locationLongitude,
          event_type: eventType,
          description: description.trim(),
          capacity: capacity ? parseInt(capacity) : null,
          is_paid: isPaid,
          price: isPaid && price ? parseFloat(price) : null,
          stripe_fee_percentage: isPaid ? 10.0 : null,
          check_in_mode: checkInMode,
          check_in_radius: checkInMode === 'LOCATION' ? (parseInt(checkInRadius, 10) || 200) : 200,
        };

        console.log('Updating event with input:', updates);
        await updateEventSupabase(existingEvent.id, updates);

        navigation.goBack();
        setTimeout(() => {
          showToast('Event updated successfully!', 'success');
        }, 100);

      } else {
        const eventData = {
          club_id: clubData.id,
          title: title.trim(),
          event_date: eventDateTime,
          event_time: formattedTime,
          end_time: eventEndDateTime,
          location: useClubLocation ? clubData.address : location.trim(),
          location_address: useClubLocation ? clubData.address : locationAddress.trim() || location.trim(),
          location_latitude: useClubLocation ? clubData.latitude : locationLatitude,
          location_longitude: useClubLocation ? clubData.longitude : locationLongitude,
          event_type: eventType,
          description: description.trim(),
          capacity: capacity ? parseInt(capacity) : null,
          is_paid: isPaid,
          price: isPaid && price ? parseFloat(price) : null,
          stripe_fee_percentage: isPaid ? 10.0 : null,
          check_in_mode: checkInMode,
          check_in_radius: checkInMode === 'LOCATION' ? (parseInt(checkInRadius, 10) || 200) : 200,
          ...(socialSourceUrl && { source_social_post_url: socialSourceUrl }),
          ...(socialSourceImageUrl && { source_social_image_url: socialSourceImageUrl }),
        };

        console.log('Creating event with input:', eventData);
        const createdEvent = await createEventSupabase(eventData);

        if (socialDraftId && createdEvent?.id) {
          try {
            await approveDraftAsEvent(socialDraftId, createdEvent.id);
          } catch (err) {
            console.error('Link draft to event failed:', err);
          }
        }

        // If user wants to save this location and it's not using club location
        if (saveAsLocation && !useClubLocation && locationLatitude && locationLongitude && locationName.trim()) {
          try {
            const { createClubLocation } = await import('../services/database');
            await createClubLocation(clubData.id, {
              name: locationName.trim(),
              address: locationAddress.trim() || location.trim() || null,
              latitude: locationLatitude,
              longitude: locationLongitude,
              notes: `Saved from event: ${title.trim()}`,
              setAsActive: false, // Don't auto-activate, let user choose
            });
            showToast('Event location saved!', 'success');
          } catch (error) {
            console.error('Error saving location:', error);
            // Don't fail the event creation if location save fails
          }
        }

        navigation.goBack();
        setTimeout(() => {
          showToast('Event created successfully!', 'success');
        }, 100);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, error);
      setLoading(false);
      showToast(`Failed to ${isEditMode ? 'update' : 'create'} event. Please try again.`, 'error');
    }
  };

  const onDateChange = (event, date) => {
    setShowDatePicker(false);
    if (date) {
      setSelectedDate(date);
    }
  };

  const onTimeChange = (event, time) => {
    setShowTimePicker(false);
    if (time) {
      setSelectedTime(time);
    }
  };

  const onEndTimeChange = (event, time) => {
    setShowEndTimePicker(false);
    if (time) {
      setSelectedEndTime(time);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (time) => {
    return time.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header - match Discover */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.borderLight, position: 'relative' }, themeShadows.small]}>
        <CheckerBackground isDarkMode={isDarkMode} />
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={[styles.backButton, { backgroundColor: colors.background + (isDarkMode ? 'AA' : '99'), paddingVertical: 10, paddingHorizontal: 12, borderRadius: 10, alignSelf: 'flex-start' }]}
        >
          <Icon name="arrow-back" size={24} color={colors.text} />
          <Text style={[styles.backText, { color: colors.text }]}>Back</Text>
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>{isEditMode ? 'Edit Event' : 'Create Event'}</Text>
      </View>

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]} showsVerticalScrollIndicator={false}>
        {/* Social Post Preview */}
        {socialSourceImageUrl && (
          <View style={[styles.section, { alignItems: 'center' }]}>
            <Image
              source={{ uri: socialSourceImageUrl }}
              style={{ width: '100%', height: 200, borderRadius: 12 }}
              resizeMode="cover"
            />
            {socialSourceUrl && (
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>
                Creating event from Instagram post
              </Text>
            )}
          </View>
        )}

        {/* Event Title */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Event Title</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
            value={title}
            onChangeText={setTitle}
            placeholder="e.g., Blitz Night"
            placeholderTextColor={colors.textLight}
          />
        </View>

        {/* Date & Time */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Date & Time</Text>
          <View style={styles.row}>
            <TouchableOpacity
              style={[styles.pickerButton, styles.dateInput, { backgroundColor: colors.surface, borderColor: colors.borderLight }]}
              onPress={() => setShowDatePicker(true)}
              activeOpacity={0.7}
            >
              <Icon name="event" size={20} color={colors.textSecondary} />
              <Text style={[styles.pickerButtonText, { color: colors.text }]}>{formatDate(selectedDate)}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.pickerButton, styles.timeInput, { backgroundColor: colors.surface, borderColor: colors.borderLight }]}
              onPress={() => setShowTimePicker(true)}
              activeOpacity={0.7}
            >
              <Icon name="schedule" size={20} color={colors.textSecondary} />
              <Text style={[styles.pickerButtonText, { color: colors.text }]}>{formatTime(selectedTime)}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.row}>
            <Text style={[styles.label, { marginBottom: 8, marginTop: 8, color: colors.text }]}>End Time (optional)</Text>
            <TouchableOpacity
              style={[styles.pickerButton, { flex: 1, backgroundColor: colors.surface, borderColor: colors.borderLight }]}
              onPress={() => setShowEndTimePicker(true)}
              activeOpacity={0.7}
            >
              <Icon name="schedule" size={20} color={colors.textSecondary} />
              <Text style={[styles.pickerButtonText, { color: colors.text }]}>{formatTime(selectedEndTime)}</Text>
            </TouchableOpacity>
          </View>

          {/* Date Picker Modal */}
          {showDatePicker && (
            Platform.OS === 'web' ? (
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  setSelectedDate(date);
                  setShowDatePicker(false);
                }}
                style={{
                  padding: 12,
                  fontSize: 16,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.borderLight,
                  marginTop: 8,
                  backgroundColor: colors.surface,
                  color: colors.text,
                }}
              />
            ) : (
              <DateTimePicker
                value={selectedDate}
                mode="date"
                display="default"
                onChange={onDateChange}
                minimumDate={new Date()}
              />
            )
          )}

          {/* Time Picker Modal */}
          {showTimePicker && (
            Platform.OS === 'web' ? (
              <input
                type="time"
                value={`${selectedTime.getHours().toString().padStart(2, '0')}:${selectedTime.getMinutes().toString().padStart(2, '0')}`}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newTime = new Date();
                  newTime.setHours(parseInt(hours));
                  newTime.setMinutes(parseInt(minutes));
                  setSelectedTime(newTime);
                  setShowTimePicker(false);
                }}
                style={{
                  padding: 12,
                  fontSize: 16,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.borderLight,
                  marginTop: 8,
                  backgroundColor: colors.surface,
                  color: colors.text,
                }}
              />
            ) : (
              <DateTimePicker
                value={selectedTime}
                mode="time"
                display="default"
                onChange={onTimeChange}
              />
            )
          )}

          {/* End Time Picker Modal */}
          {showEndTimePicker && (
            Platform.OS === 'web' ? (
              <input
                type="time"
                value={`${selectedEndTime.getHours().toString().padStart(2, '0')}:${selectedEndTime.getMinutes().toString().padStart(2, '0')}`}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newTime = new Date();
                  newTime.setHours(parseInt(hours));
                  newTime.setMinutes(parseInt(minutes));
                  setSelectedEndTime(newTime);
                  setShowEndTimePicker(false);
                }}
                style={{
                  padding: 12,
                  fontSize: 16,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.borderLight,
                  marginTop: 8,
                  backgroundColor: colors.surface,
                  color: colors.text,
                }}
              />
            ) : (
              <DateTimePicker
                value={selectedEndTime}
                mode="time"
                display="default"
                onChange={onEndTimeChange}
              />
            )
          )}
        </View>

        {/* Location */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Location</Text>
          <TouchableOpacity
            style={styles.locationToggle}
            onPress={() => {
              setUseClubLocation(!useClubLocation);
              if (!useClubLocation && clubData?.address) {
                setLocation(clubData.address);
              }
            }}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, { borderColor: colors.primary }]}>
              {useClubLocation && <Icon name="check" size={18} color={colors.primary} />}
            </View>
            <Text style={[styles.locationToggleText, { color: colors.text }]}>Use Club Location</Text>
          </TouchableOpacity>
          {!useClubLocation && (
            <>
              <TextInput
                style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
                value={location}
                onChangeText={setLocation}
                placeholder="Enter custom location"
                placeholderTextColor={colors.textLight}
                multiline
              />
              <TouchableOpacity
                style={[styles.mapPickerButton, { borderColor: colors.primary, backgroundColor: colors.primary + '18' }]}
                onPress={() => {
                  navigation.navigate('MapPicker', {
                    onLocationSelect: (coords, address) => {
                      setLocationLatitude(coords.latitude);
                      setLocationLongitude(coords.longitude);
                      if (address) {
                        setLocationAddress(address);
                        setLocation(address);
                      }
                    },
                  });
                }}
              >
                <Icon name="map" size={20} color={colors.primary} />
                <Text style={[styles.mapPickerButtonText, { color: colors.text }]}>Pick on Map</Text>
              </TouchableOpacity>
            </>
          )}
          {useClubLocation && clubData?.address && (
            <Text style={[styles.locationPreview, { color: colors.textSecondary }]}>{clubData.address}</Text>
          )}
          {!useClubLocation && locationLatitude && locationLongitude && (
            <View style={styles.saveLocationOption}>
              <TouchableOpacity
                style={[styles.saveLocationToggle, { borderColor: colors.primary }]}
                onPress={() => setSaveAsLocation(!saveAsLocation)}
              >
                {saveAsLocation && <Icon name="check" size={18} color={colors.primary} />}
              </TouchableOpacity>
              <Text style={[styles.saveLocationToggleText, { color: colors.textSecondary }]}>Save this location</Text>
            </View>
          )}
          {saveAsLocation && !useClubLocation && (
            <TextInput
              style={[styles.locationNameInput, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
              placeholder="Location name (e.g., 'Weekly Bar Night')"
              value={locationName}
              onChangeText={setLocationName}
              placeholderTextColor={colors.textLight}
            />
          )}
        </View>

        {/* Event Type */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Event Type</Text>
          <View style={styles.eventTypeContainer}>
            {['CASUAL', 'BLITZ', 'TOURNAMENT', 'CLASS'].map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.eventTypeButton,
                  { backgroundColor: colors.surface, borderColor: colors.borderLight },
                  eventType === type && [styles.eventTypeButtonActive, { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
                ]}
                onPress={() => setEventType(type)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.eventTypeText,
                    { color: colors.textSecondary },
                    eventType === type && [styles.eventTypeTextActive, { color: '#fff' }],
                  ]}
                >
                  {type.charAt(0) + type.slice(1).toLowerCase()}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Check-in */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Check-in at event</Text>
          <View style={styles.eventTypeContainer}>
            {['OFF', 'QR_CODE', 'LOCATION'].map((mode) => (
              <TouchableOpacity
                key={mode}
                style={[
                  styles.eventTypeButton,
                  { backgroundColor: colors.surface, borderColor: colors.borderLight },
                  checkInMode === mode && [styles.eventTypeButtonActive, { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
                ]}
                onPress={() => setCheckInMode(mode)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.eventTypeText,
                    { color: colors.textSecondary },
                    checkInMode === mode && [styles.eventTypeTextActive, { color: '#fff' }],
                  ]}
                >
                  {mode === 'OFF' ? 'Off' : mode === 'QR_CODE' ? 'QR code' : 'Location'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {checkInMode === 'LOCATION' && (
            <View style={{ marginTop: 12 }}>
              <Text style={[styles.label, { color: colors.text }]}>Radius (meters)</Text>
              <TextInput
                style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
                value={String(checkInRadius)}
                onChangeText={(t) => setCheckInRadius(t.replace(/\D/g, '') || '200')}
                placeholder="200"
                placeholderTextColor={colors.textLight}
                keyboardType="number-pad"
              />
              <Text style={[styles.hint, { color: colors.textSecondary }]}>Estimated turnout using nearby player locations within this distance.</Text>
            </View>
          )}
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Description</Text>
          <TextInput
            style={[styles.input, styles.textArea, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
            value={description}
            onChangeText={setDescription}
            placeholder="Event details, rules, what to bring, etc."
            placeholderTextColor={colors.textLight}
            multiline
            numberOfLines={4}
          />
        </View>

        {/* Capacity */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Capacity (optional)</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
            value={capacity}
            onChangeText={setCapacity}
            placeholder="Maximum number of attendees"
            placeholderTextColor={colors.textLight}
            keyboardType="number-pad"
          />
        </View>

        {/* Event Payment */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Event Type</Text>
          <View style={styles.paymentTypeContainer}>
            <TouchableOpacity
              style={[
                styles.paymentTypeButton,
                { backgroundColor: colors.surface, borderColor: colors.borderLight },
                !isPaid && [styles.paymentTypeButtonActive, { backgroundColor: QUICK_PAIR_GREEN + '22', borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
              ]}
              onPress={() => {
                setIsPaid(false);
                setPrice('');
              }}
              activeOpacity={0.7}
            >
              <Icon name="check-circle" size={20} color={!isPaid ? colors.primary : colors.textLight} />
              <Text style={[styles.paymentTypeText, { color: colors.textSecondary }, !isPaid && [styles.paymentTypeTextActive, { color: colors.text }]]}>
                Free Event
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.paymentTypeButton,
                { backgroundColor: colors.surface, borderColor: colors.borderLight },
                isPaid && [styles.paymentTypeButtonActive, { backgroundColor: QUICK_PAIR_GREEN + '22', borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
              ]}
              onPress={() => setIsPaid(true)}
              activeOpacity={0.7}
            >
              <Icon name="attach-money" size={20} color={isPaid ? colors.primary : colors.textLight} />
              <Text style={[styles.paymentTypeText, { color: colors.textSecondary }, isPaid && [styles.paymentTypeTextActive, { color: colors.text }]]}>
                Paid Event
              </Text>
            </TouchableOpacity>
          </View>
          {isPaid && (
            <View style={[styles.priceInputContainer, { backgroundColor: colors.surface, borderColor: colors.borderLight }]}>
              <Text style={[styles.priceLabel, { color: colors.text }]}>Entry Fee</Text>
              <View style={[styles.priceInputWrapper, { borderColor: colors.borderLight, backgroundColor: colors.background }]}>
                <Text style={[styles.currencySymbol, { color: colors.text }]}>$</Text>
                <TextInput
                  style={[styles.priceInput, { color: colors.text }]}
                  value={price}
                  onChangeText={setPrice}
                  placeholder="0.00"
                  placeholderTextColor={colors.textLight}
                  keyboardType="decimal-pad"
                />
              </View>
              <Text style={[styles.feeNote, { color: colors.textSecondary }]}>
                Platform fee: 10% • You receive: ${price ? (parseFloat(price) * 0.9).toFixed(2) : '0.00'}
              </Text>
            </View>
          )}
        </View>

        {/* Create Button - match Discover primary action */}
        <TouchableOpacity
          style={[styles.createButton, { backgroundColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW, loading && styles.createButtonDisabled]}
          onPress={handleCreateEvent}
          disabled={loading}
          activeOpacity={0.8}
        >
          {loading ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <Text style={styles.createButtonText}>{isEditMode ? 'Update Event' : 'Create Event'}</Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  backText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
    color: '#333',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  dateInput: {
    flex: 2,
  },
  timeInput: {
    flex: 1,
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#f9f9f9',
  },
  pickerButtonText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  locationToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#94EB98',
    borderRadius: 4,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationToggleText: {
    fontSize: 16,
    color: '#333',
  },
  locationPreview: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },
  saveLocationOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    gap: 8,
  },
  saveLocationToggle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#94EB98',
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveLocationToggleText: {
    fontSize: 14,
    color: '#666',
  },
  locationNameInput: {
    marginTop: 8,
    padding: 12,
    fontSize: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f9f9f9',
  },
  mapPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#94EB98',
    backgroundColor: '#f0f9f0',
  },
  mapPickerButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  eventTypeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  eventTypeButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f9f9f9',
  },
  eventTypeButtonActive: {
    backgroundColor: '#94EB98',
    borderColor: '#94EB98',
  },
  eventTypeText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  eventTypeTextActive: {
    color: '#333',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  createButton: {
    backgroundColor: '#94EB98',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 40,
  },
  createButtonDisabled: {
    opacity: 0.6,
  },
  createButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
  },
  paymentTypeContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  paymentTypeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#ddd',
    backgroundColor: '#f9f9f9',
  },
  paymentTypeButtonActive: {
    backgroundColor: '#e8f5e9',
    borderColor: '#94EB98',
  },
  paymentTypeText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  paymentTypeTextActive: {
    color: '#333',
  },
  priceInputContainer: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  priceLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  priceInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 8,
  },
  priceInput: {
    flex: 1,
    padding: 12,
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  feeNote: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },
});

export default CreateEventScreen;

